/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryApi, BaseQueryFn, FetchArgs } from "@reduxjs/toolkit/query";
import { API_TAGS } from "@/api/abstractions/api-types";
import { config } from "@/config/environment";
import { getMsalInstance } from "@/config/auth-config";
import { MsalService } from "@/components/security/msal-service";
import { unwrapApiEnvelope } from "@/utilities/api-utils";
import { getReturnUrl, isSafeReturnPath } from "@/utilities/return-url";
import { clearActiveAccount } from "@/utilities/msal-utils";

type FetchArgsLike = FetchArgs | string;

function withAuthorizationHeader(
  args: FetchArgsLike,
  token: string
): FetchArgs {
  if (typeof args === "string") {
    return {
      url: args,
      headers: { authorization: `Bearer ${token}` }
    };
  }

  const headers = new Headers(args.headers as HeadersInit | undefined);
  headers.set("authorization", `Bearer ${token}`);
  return {
    ...args,
    headers: Object.fromEntries(headers.entries())
  };
}

function redirectToLogin(): void {
  const instance = getMsalInstance();
  clearActiveAccount(instance);

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const safeCurrent =
    isSafeReturnPath(currentPath) && currentPath !== "/login"
      ? currentPath
      : null;
  const returnPath = getReturnUrl() ?? safeCurrent;
  const loginUrl = returnPath
    ? `/login?returnUrl=${encodeURIComponent(returnPath)}`
    : "/login";

  window.location.assign(loginUrl);
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  const instance = getMsalInstance();
  const msalService = MsalService.getInstance(instance);
  const account = msalService.getActiveAccount();
  if (!account) {
    return null;
  }

  return msalService.acquireTokenSilent(account, { forceRefresh: true });
}

const createAuthenticatedBaseQuery = (): BaseQueryFn => {
  const baseQuery = fetchBaseQuery({
    baseUrl: config.api.baseUrl,
    timeout: config.api.timeout
  });

  return async (args, api: BaseQueryApi, extraOptions) => {
    const resolvedArgs =
      typeof args === "object" && args !== null ? await args : args;

    const result = await baseQuery(resolvedArgs, api, extraOptions);
    const unwrapped = unwrapApiEnvelope(result);

    if (unwrapped.error && unwrapped.error.status === 401) {
      const token = await refreshAccessTokenOnce();
      if (!token) {
        redirectToLogin();
        return unwrapped;
      }

      const retryArgs = withAuthorizationHeader(
        resolvedArgs as FetchArgsLike,
        token
      );
      const retryResult = await baseQuery(retryArgs, api, extraOptions);
      const retryUnwrapped = unwrapApiEnvelope(retryResult);

      if (retryUnwrapped.error && retryUnwrapped.error.status === 401) {
        redirectToLogin();
      }

      return retryUnwrapped;
    }

    return unwrapped;
  };
};

export const authenticatedApiSlice = createApi({
  reducerPath: "authenticatedApi",
  baseQuery: createAuthenticatedBaseQuery(),
  tagTypes: Object.values(API_TAGS),
  endpoints: () => ({}),
  keepUnusedDataFor: config.api.keepUnusedDataFor,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false
});

const store = configureStore({
  reducer: {
    [authenticatedApiSlice.reducerPath]: authenticatedApiSlice.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(authenticatedApiSlice.middleware),
  devTools: config.debug.reduxDevTools && config.app.env !== "production"
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export function ApiProvider({ children }: { children: ReactNode }) {
  return <Provider store={store}>{children}</Provider>;
}
